import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import TextArea from '../../../components/TextArea';
import DatePicker from '../../../components/DatePicker';
import Switch from '../../../components/Switch';
import { formatDateForInput } from '../../../utils/dateUtils';
import { getCodeError, getNameError, getHtmlTagsError, getModelSerialNumberError, getDateError } from '../../../utils/validation';

function AddDeviceModal({
    open,
    onClose,
    onSubmit,
    editMode = false,
    deviceData = null,
    initDevice = null,
    availableUnits = [],
    availableDeviceTypes = [],
    availableVerifyModes = [],
    availableConnectionStatuses = [],
}) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        deviceCode: '',
        name: '',
        deviceType: '',
        verifyModeId: '',
        model: '',
        serialNumber: '',
        registrationDate: '',
        unitId: '',
        approvedDate: '',
        isValid: false,
        connectionStatus: 0,
        maxCallToCSDL: 0,
    });
    const [isInitialized, setIsInitialized] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Initialize form data only once when modal opens
        if (!open) {
            setIsInitialized(false);
            setErrors({});
            return;
        }

        if (isInitialized) return;

        if (editMode && deviceData) {
            console.log('Edit mode - deviceData received:', deviceData);
            console.log('Edit mode - deviceData.verifyModeId:', deviceData.verifyModeId);
            setFormData({
                id: deviceData.id,
                deviceCode: deviceData.deviceCode || '',
                name: deviceData.name || '',
                deviceType: deviceData.deviceType ?? '', // Use nullish coalescing to handle 0 value
                verifyModeId: deviceData.verifyModeId ?? '', // Use nullish coalescing to handle 0 value
                model: deviceData.model || '',
                serialNumber: deviceData.serialNumber || '',
                registrationDate: formatDateForInput(deviceData.registrationDate),
                unitId: deviceData.unitId || '',
                approvedDate: formatDateForInput(deviceData.approvedDate),
                isValid: deviceData.isValid ?? true, // Use nullish coalescing to handle false values correctly
                connectionStatus: deviceData.connectionStatus ?? 0,
                maxCallToCSDL: deviceData.maxCallToCSDL ?? '',
            });
            setIsInitialized(true);
        } else if (!editMode) {
            // Add mode - initialize empty form with current date
            const now = new Date();

            setFormData({
                deviceCode: '',
                name: '',
                deviceType: '',
                verifyModeId: '',
                model: '',
                serialNumber: '',
                registrationDate: formatDateForInput(now.toISOString()),
                unitId: '',
                approvedDate: '',
                isValid: false, // Default to false for new devices
                connectionStatus: 0, // Default to Offline
                maxCallToCSDL: 0, // Default to 0
            });
            setIsInitialized(true);
        }
    }, [open, editMode, deviceData, initDevice, isInitialized]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // For switch/checkbox fields, always use 'checked' property
        // List all boolean fields that use Switch component
        const booleanFields = ['isValid'];
        const fieldValue = booleanFields.includes(name) ? checked : value;

        // Debug log to check what we're getting
        if (name === 'isValid') {
            console.log('isValid handleChange:', {
                name,
                value,
                type,
                checked,
                fieldValue,
            });
        }

        setFormData((prev) => {
            const newFormData = {
                ...prev,
                [name]: fieldValue,
            };

            // When approvedDate is set, automatically set isValid to true
            if (name === 'approvedDate' && value) {
                newFormData.isValid = true;
            }

            return newFormData;
        });

        // Validate field in real-time
        const newErrors = { ...errors };

        if (name === 'deviceCode') {
            const codeError = getCodeError(value, t);
            if (codeError) {
                newErrors.deviceCode = codeError;
            } else {
                delete newErrors.deviceCode;
            }
        } else if (name === 'name') {
            const nameError = getNameError(value, t);
            if (nameError) {
                newErrors.name = nameError;
            } else {
                delete newErrors.name;
            }
        } else if (name === 'model') {
            const modelError = getModelSerialNumberError(value, t);
            if (modelError) {
                newErrors.model = modelError;
            } else {
                delete newErrors.model;
            }
        } else if (name === 'serialNumber') {
            const serialError = getModelSerialNumberError(value, t);
            if (serialError) {
                newErrors.serialNumber = serialError;
            } else {
                delete newErrors.serialNumber;
            }
        } else if (name === 'registrationDate') {
            const dateError = getDateError(value, t, { required: true, noFuture: true, simpleMessage: true });
            if (dateError) {
                newErrors.registrationDate = dateError;
            } else {
                // Check if registrationDate is after approvedDate
                if (value && formData.approvedDate) {
                    const regDate = new Date(value);
                    const approvedDate = new Date(formData.approvedDate);
                    if (regDate > approvedDate) {
                        newErrors.registrationDate = t('devices.validation.registrationDateAfterApproved');
                    } else {
                        delete newErrors.registrationDate;
                    }
                } else {
                    delete newErrors.registrationDate;
                }
            }
        }

        setErrors(newErrors);
    };

    const handleSwitchChange = (e) => {
        const { name, checked } = e.target;
        console.log('handleSwitchChange:', { name, checked });
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        const codeError = getCodeError(formData.deviceCode, t);
        if (codeError) newErrors.deviceCode = codeError;

        const nameError = getNameError(formData.name, t);
        if (nameError) newErrors.name = nameError;

        // Validate model
        const modelError = getModelSerialNumberError(formData.model, t);
        if (modelError) newErrors.model = modelError;

        // Validate serialNumber
        const serialError = getModelSerialNumberError(formData.serialNumber, t);
        if (serialError) newErrors.serialNumber = serialError;

        // Validate registrationDate
        const registrationDateError = getDateError(formData.registrationDate, t, { required: true, noFuture: true, simpleMessage: true });
        if (registrationDateError) {
            newErrors.registrationDate = registrationDateError;
        } else if (formData.registrationDate && formData.approvedDate) {
            // Check if registrationDate is after approvedDate
            const regDate = new Date(formData.registrationDate);
            const approvedDate = new Date(formData.approvedDate);
            if (regDate > approvedDate) {
                newErrors.registrationDate = t('devices.validation.registrationDateAfterApproved');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Check if all required fields are filled and no validation errors
    const isFormValid = () => {
        // Check required fields
        const requiredFieldsValid = formData.deviceCode.trim() !== '' && formData.name.trim() !== '' && formData.deviceType !== '';

        // Check if there are any validation errors
        const noValidationErrors = Object.keys(errors).length === 0;

        return requiredFieldsValid && noValidationErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Clean up data before submitting
        const submitData = {
            ...formData,
            // Convert isValid to boolean (handles "on", true, false, etc.)
            isValid: formData.isValid === true || formData.isValid === 'on' || formData.isValid === 'true',
            // Convert empty string unitId to null
            unitId: formData.unitId === '' ? null : formData.unitId,
            // Convert empty string verifyModeId to null
            verifyModeId: formData.verifyModeId === '' ? null : formData.verifyModeId,
            // Convert empty string approvedDate to null
            approvedDate: formData.approvedDate === '' ? null : formData.approvedDate,
            // Convert maxCallToCSDL to number (0 = unlimited)
            maxCallToCSDL: formData.maxCallToCSDL === '' ? 0 : Number(formData.maxCallToCSDL),
        };

        console.log('handleSubmit - formData:', formData);
        console.log('handleSubmit - submitData:', submitData);
        console.log('isValid type:', typeof submitData.isValid, 'value:', submitData.isValid);
        console.log('unitId type:', typeof submitData.unitId, 'value:', submitData.unitId);
        console.log('verifyModeId type:', typeof submitData.verifyModeId, 'value:', submitData.verifyModeId);

        onSubmit(submitData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'primary.main',
                }}
            >
                {editMode ? <EditIcon /> : <AddIcon />}
                {editMode ? t('devices.editDialog.title') : t('devices.addDialog.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
                    <Input
                        label={t('devices.addDialog.deviceCode')}
                        name="deviceCode"
                        value={formData.deviceCode}
                        onChange={handleChange}
                        required={true}
                        disabled={editMode}
                        error={!!errors.deviceCode}
                        helperText={errors.deviceCode || (editMode ? t('devices.addDialog.codeEditDisabled') : '')}
                        id="deviceCode"
                        data-testid="device-modal-input-deviceCode"
                    />
                    <Input
                        label={t('devices.addDialog.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required={true}
                        error={!!errors.name}
                        helperText={errors.name}
                        id="name"
                        data-testid="device-modal-input-name"
                    />
                    <Select
                        label={t('devices.addDialog.deviceType')}
                        name="deviceType"
                        value={formData.deviceType}
                        onChange={handleChange}
                        options={availableDeviceTypes}
                        required={true}
                        id="deviceType"
                        data-testid="device-modal-select-deviceType"
                    />
                    <Select
                        label={t('devices.addDialog.verifyMode')}
                        name="verifyModeId"
                        value={formData.verifyModeId}
                        onChange={handleChange}
                        options={availableVerifyModes}
                        id="verifyModeId"
                        data-testid="device-modal-select-verifyModeId"
                    />
                    <Input
                        label={t('devices.addDialog.model')}
                        name="model"
                        value={formData.model}
                        onChange={handleChange}
                        error={!!errors.model}
                        helperText={errors.model}
                        id="model"
                        data-testid="device-modal-input-model"
                    />
                    <Input
                        label={t('devices.addDialog.serialNumber')}
                        name="serialNumber"
                        value={formData.serialNumber}
                        onChange={handleChange}
                        error={!!errors.serialNumber}
                        helperText={errors.serialNumber}
                        id="serialNumber"
                        data-testid="device-modal-input-serialNumber"
                    />
                    <DatePicker
                        label={t('devices.addDialog.registrationDate')}
                        name="registrationDate"
                        value={formData.registrationDate}
                        onChange={handleChange}
                        required={true}
                        error={!!errors.registrationDate}
                        helperText={errors.registrationDate || (formData.approvedDate ? t('devices.addDialog.registrationDateHelper') : '')}
                        maxDate={formData.approvedDate || undefined}
                        id="registrationDate"
                        data-testid="device-modal-input-registrationDate"
                    />
                    <Select
                        label={t('devices.addDialog.unitName')}
                        name="unitId"
                        value={formData.unitId}
                        onChange={handleChange}
                        options={availableUnits}
                        id="unitId"
                        data-testid="device-modal-select-unitId"
                    />
                    <Switch
                        label={t('devices.addDialog.isValid')}
                        name="isValid"
                        checked={formData.isValid}
                        onChange={handleSwitchChange}
                        disabled={!formData.approvedDate}
                        id="isValid"
                        data-testid="device-modal-switch-isValid"
                    />
                    <Select
                        label={t('devices.addDialog.connectionStatus')}
                        name="connectionStatus"
                        value={formData.connectionStatus}
                        onChange={handleChange}
                        options={availableConnectionStatuses}
                        id="connectionStatus"
                        data-testid="device-modal-select-connectionStatus"
                    />
                    <Input
                        label={t('devices.addDialog.maxCallToCSDL')}
                        name="maxCallToCSDL"
                        value={formData.maxCallToCSDL}
                        onChange={handleChange}
                        type="number"
                        id="maxCallToCSDL"
                        data-testid="device-modal-input-maxCallToCSDL"
                    />
                    <DatePicker
                        label={t('devices.addDialog.approvedDate')}
                        name="approvedDate"
                        value={formData.approvedDate}
                        onChange={handleChange}
                        disabled={true}
                        helperText={t('devices.addDialog.approvedDateReadOnly')}
                        id="approvedDate"
                        data-testid="device-modal-input-approvedDate"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="outlined" id="cancel" name="cancel" data-testid="device-modal-button-cancel">
                    {t('devices.addDialog.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={!isFormValid()}
                    startIcon={editMode ? <EditIcon /> : <AddIcon />}
                    id="confirm"
                    name="confirm"
                    data-testid="device-modal-button-confirm"
                >
                    {editMode ? t('devices.addDialog.update') : t('devices.addDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddDeviceModal;
